// The space train's route: a banking, climbing 3D loop whose near side sweeps past the main
// island (~118 m out, ~70 m up, in front of the inner mountain range) and whose far side
// climbs behind the ranges into the haze.
// Arc-length parameterised (1 m table) so cars sit at exact coupling distances.
// Tuned by CURVATURE, not amplitude (CLAUDE.md gotcha 14): minimum radius ~470 m keeps the
// angle between neighbouring 17 m cars under ~2.2°, while lateral S-bends keep it from
// reading as a rigid cut-out.

import * as THREE from 'three';

export class TrainPath {
  readonly length: number;
  private pts: Float32Array; // x,y,z per metre
  private n: number;

  /** rot: direction (atan2(z, x)) from the island toward the loop's centre, so the near pass
   *  crosses in front of whoever looks that way. Default: per-seed. */
  constructor(seed: number, rot = ((seed % 360) / 360) * Math.PI * 2, dNear = 118, yNear = 70, k = 1) {
    // An ellipse whose near side sweeps past the island (dNear m beyond its centre, yNear up),
    // in front of the inner mountain range (215 m), its far side behind the ranges in the haze.
    // k scales the loop with the train so the per-car kink (curvature × car pitch) is unchanged
    const A = 655 * k, B = 615 * k;
    const nx = Math.cos(rot), nz = Math.sin(rot); // centre direction from the island
    const cx = nx * (dNear + B), cz = nz * (dNear + B);
    const raw = (u: number, out: THREE.Vector3) => {
      const lx = A * Math.sin(u);
      const lz = B * Math.cos(u);
      const y = yNear + k * (52 * (1 - Math.cos(u)) - 8 * Math.sin(2 * u));
      // local z points from the centre toward the island; local x along the pass
      return out.set(cx - nx * lz - nz * lx, y, cz - nz * lz + nx * lx);
    };
    // dense sample then resample at 1 m
    const M = 20000;
    const dense = new Float32Array((M + 1) * 3);
    const cum = new Float32Array(M + 1);
    const v = new THREE.Vector3(), p = new THREE.Vector3();
    for (let i = 0; i <= M; i++) {
      raw((i / M) * Math.PI * 2, v);
      dense[i * 3] = v.x; dense[i * 3 + 1] = v.y; dense[i * 3 + 2] = v.z;
      if (i > 0) cum[i] = cum[i - 1] + v.distanceTo(p);
      p.copy(v);
    }
    this.length = cum[M];
    this.n = Math.floor(this.length);
    this.pts = new Float32Array((this.n + 2) * 3);
    let j = 0;
    for (let s = 0; s <= this.n + 1; s++) {
      const ss = Math.min(s * (this.length / this.n), this.length);
      while (j < M - 1 && cum[j + 1] < ss) j++;
      const t = (ss - cum[j]) / Math.max(1e-6, cum[j + 1] - cum[j]);
      for (let c = 0; c < 3; c++) this.pts[s * 3 + c] = dense[j * 3 + c] + (dense[(j + 1) * 3 + c] - dense[j * 3 + c]) * t;
    }
  }

  /** Position at arc length s (wraps). */
  at(s: number, out: THREE.Vector3) {
    const L = this.length;
    let u = (s % L + L) % L;
    u *= this.n / L;
    const i = Math.floor(u), t = u - i;
    const P = this.pts, a = i * 3, b = (i + 1) * 3;
    return out.set(P[a] + (P[b] - P[a]) * t, P[a + 1] + (P[b + 1] - P[a + 1]) * t, P[a + 2] + (P[b + 2] - P[a + 2]) * t);
  }
}
